import React, { useContext, useEffect, useMemo, useState } from "react";
import { CategoriesCondimentsStyled } from "./CategoriesCondimentsStyled";
import Categories from "../../components/Categories";
import CondimentsList from "../../components/Condiment/CondimentList/CondimentList";
import Box from "../../UI/Box";
import Filter from "../../components/Filter";
import { VIEW_TYPE, SORTING_TYPE, OPTION_TYPE } from "../../entities/Filter";
import {
  CategoryInterface,
  optionsWhitelistedParentIds,
} from "../../entities/Category";
import { useAppDispatch } from "../../store/store";
import { setMixtureName } from "../../components/Mixture/MixtureSlice";
import { setBreadcrumbs } from "../../components/Breadcrumbs/slice/BreadcrumbsSlice";
import { useSelector } from "react-redux";
import { getSelectedCondiments } from "../../components/Condiment/selectors";
import { getDataFromMixture } from "../../components/Mixture/selectors";
import { onOpenModal } from "../../components/Modal/slice/ModalSlice";
import { MODAL_TYPES } from "../../entities/Modal";
import { getValueForKey } from "../../context/translationsUtils";
import { TranslationContextInterface } from "../../entities/Language";
import { TranslationContext } from "../../context/TranslationContext";
import { useParams } from "react-router-dom";
import { clearSelectedCondiments } from "../../components/Condiment/CondimentSlice";
import { fetchMixesByFuchsByCategoryId } from "../../components/MixByFuchs/actions";

const CategoriesCondiments: React.FC = () => {
  const dispatch = useAppDispatch();
  const [sort, setSort] = useState<SORTING_TYPE>(SORTING_TYPE.ASC);
  const [view, setView] = useState<VIEW_TYPE>(VIEW_TYPE.GRID);
  const [option, setOption] = useState<OPTION_TYPE>(OPTION_TYPE.PREDEFINED);
  const { translations }: TranslationContextInterface =
    useContext(TranslationContext);
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryInterface | null>(null);
  const [disableSelected, setDisableSelected] = useState<boolean>(true);
  const selectedCondiments = useSelector(getSelectedCondiments);
  const { condimentLimitPerMix } = useSelector(getDataFromMixture);
  const { parentId } = useParams<{ parentId: string }>();
  useEffect(() => {
    if (selectedCondiments.length >= condimentLimitPerMix) {
      setDisableSelected(false);
      dispatch(
        onOpenModal({
          modalType: MODAL_TYPES.ALERT,
          content: {
            name: getValueForKey(translations, "alerts.alert_title"),
            description: `${getValueForKey(
              translations,
              "alerts.condiment_limit_reached",
            )} ${condimentLimitPerMix}`,
          },
        }),
      );
    } else {
      setDisableSelected(true);
    }
  }, [condimentLimitPerMix, selectedCondiments.length, dispatch, translations]);

  const setActiveCategory = (category: CategoryInterface) => {
    setActiveCategoryId(category.id);
    setSelectedCategory(category);
    dispatch(setMixtureName(category.name));
    dispatch(setBreadcrumbs({ name: category.name, level: 1, link: null }));
  };

  useEffect(() => {
    dispatch(clearSelectedCondiments());
  }, [dispatch, parentId]);

  const isOptionsWhitelistedParentId = useMemo(
    () => optionsWhitelistedParentIds.includes(parentId ?? ""),
    [parentId],
  );

  useEffect(() => {
    if (isOptionsWhitelistedParentId && activeCategoryId) {
      dispatch(
        fetchMixesByFuchsByCategoryId({
          params: { categoryId: activeCategoryId, limit: 100 },
        }),
      );
    }
  }, [activeCategoryId]);

  return (
    <CategoriesCondimentsStyled>
      <Categories
        activeCategoryId={activeCategoryId}
        setActiveCategory={(category) => setActiveCategory(category)}
      />
      <Box>
        <Filter
          sort={sort}
          view={view}
          setSort={setSort}
          setView={setView}
          option={isOptionsWhitelistedParentId ? option : undefined}
          setOption={isOptionsWhitelistedParentId ? setOption : undefined}
        />
        {activeCategoryId && (
          <CondimentsList
            isPredefinedOption={option === OPTION_TYPE.PREDEFINED}
            sort={sort}
            view={view}
            categoryId={activeCategoryId}
            isCategoriesCondiments={true}
            categoryName={selectedCategory?.name}
            disableSelected={disableSelected}
          />
        )}
      </Box>
    </CategoriesCondimentsStyled>
  );
};

export default CategoriesCondiments;
